// Send time of a planned message: proposals relative to the maintenance start
// and the "YYYY-MM-DDTHH:MM" local value the send bar keeps.
import { parseGermanDate } from "@/lib/enValues";

export const PROPOSAL_HOUR = 10;
export const WEEKDAY_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const WEEKDAY_LONG = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

const pad = (n: number) => String(n).padStart(2, "0");

export function toLocalValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromLocalValue(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function atTime(day: Date, hours: number, minutes = 0): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    hours,
    minutes,
  );
}

function addDays(day: Date, days: number): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate() + days,
    day.getHours(),
    day.getMinutes(),
  );
}

/** Maintenance start from the {{date}} and {{start}} values, e.g. "Dienstag, 27.10.2026" and "08:00 Uhr". */
export function maintenanceStart(
  date: string | undefined,
  start: string | undefined,
  today = new Date(),
): Date | null {
  const parsed = parseGermanDate(date ?? "");
  if (!parsed) return null;
  const time = /(\d{1,2})[:.](\d{2})/.exec(start ?? "");
  const hours = time ? Number(time[1]) : 0;
  const minutes = time ? Number(time[2]) : 0;
  if (hours > 23 || minutes > 59) return null;
  const result = new Date(
    parsed.year ?? today.getFullYear(),
    parsed.month - 1,
    parsed.day,
    hours,
    minutes,
  );
  return result.getMonth() === parsed.month - 1 ? result : null;
}

export type Proposal = { key: string; label: string; at: Date; note?: string };

/**
 * "3 Tage vorher" at 10:00, the workday before that when it falls on a
 * weekend, and "1 Tag vorher".
 */
export function proposals(start: Date): Proposal[] {
  const threeDays = atTime(addDays(start, -3), PROPOSAL_HOUR);
  const weekend = threeDays.getDay() === 0 || threeDays.getDay() === 6;
  const list: Proposal[] = [
    {
      key: "3d",
      label: "3 Tage vorher",
      at: threeDays,
      note: weekend
        ? `Fällt auf einen ${WEEKDAY_LONG[threeDays.getDay()]}`
        : undefined,
    },
  ];
  if (weekend) {
    let workday = threeDays;
    while (workday.getDay() === 0 || workday.getDay() === 6)
      workday = addDays(workday, -1);
    list.push({ key: "workday", label: "Letzter Werktag davor", at: workday });
  }
  list.push({
    key: "1d",
    label: "1 Tag vorher",
    at: atTime(addDays(start, -1), PROPOSAL_HOUR),
  });
  return list;
}

/** "Sa 24.10. 10:00" */
export function formatShort(date: Date): string {
  return `${WEEKDAY_SHORT[date.getDay()]} ${pad(date.getDate())}.${pad(date.getMonth() + 1)}. ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "Sa, 24.10.2026 · 10:00 Uhr" */
export function formatLong(date: Date): string {
  return `${WEEKDAY_SHORT[date.getDay()]}, ${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} · ${pad(date.getHours())}:${pad(date.getMinutes())} Uhr`;
}

/** "4 Tage und 18 Stunden", "2 Stunden", "15 Minuten" */
export function formatSpan(ms: number): string {
  const minutes = Math.round(Math.abs(ms) / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const plural = (n: number, one: string, many: string) =>
    `${n} ${n === 1 ? one : many}`;
  if (days && hours)
    return `${plural(days, "Tag", "Tage")} und ${plural(hours, "Stunde", "Stunden")}`;
  if (days) return plural(days, "Tag", "Tage");
  if (hours) return plural(hours, "Stunde", "Stunden");
  return plural(minutes, "Minute", "Minuten");
}
